import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../../../config/firebase';
import { 
  Plus, Search, Filter, Edit2, Trash2, DoorOpen, 
  RefreshCw, AlertCircle, CheckCircle, Info, LayoutGrid, List 
} from 'lucide-react';
import './RoomInventoryTab.css';

const RoomInventoryTab = ({ hotelId, token }) => {
  const [rooms, setRooms] = useState([]);
  const [floors, setFloors] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterFloor, setFilterFloor] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [stats, setStats] = useState({});
  const [statusOverrides, setStatusOverrides] = useState({});

  const getAuthHeaders = async (forceRefresh = false) => {
    const currentUser = auth.currentUser;

    if (currentUser) {
      const idToken = await currentUser.getIdToken(forceRefresh);
      return { Authorization: `Bearer ${idToken}` };
    }

    const fallbackToken =
      token ||
      localStorage.getItem('authToken') ||
      sessionStorage.getItem('authToken') ||
      localStorage.getItem('token') ||
      sessionStorage.getItem('token');

    if (!fallbackToken) {
      throw new Error('Authentication token unavailable');
    }

    const sanitized = String(fallbackToken)
      .replace(/^Bearer\s+/i, '')
      .replace(/^"+|"+$/g, '')
      .trim();

    return { Authorization: `Bearer ${sanitized}` };
  };

  const requestWithAuth = async (requestFactory) => {
    try {
      const headers = await getAuthHeaders(false);
      return await requestFactory(headers);
    } catch (err) {
      if (err?.response?.status === 401 && auth.currentUser) {
        const headers = await getAuthHeaders(true);
        return requestFactory(headers);
      }
      throw err;
    }
  };

  useEffect(() => {
    if (hotelId) {
      fetchRooms();
      fetchFloors();
      fetchRoomTypes();
      fetchStats();
    }
  }, [hotelId, token]);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().split('T')[0];
      const response = await requestWithAuth((headers) => axios.get(
        `${process.env.REACT_APP_API_URL}/api/manager/rooms`,
        {
          headers,
          params: {
            date: today,
            _ts: Date.now()
          }
        }
      ));
      const roomsData = response.data?.data?.rooms || response.data?.data || response.data || [];
      
      // Debug: Log raw response and parsed rooms
      console.log('[RoomInventoryTab] Raw API response:', response.data);
      console.log('[RoomInventoryTab] Fetched rooms:', roomsData.map(r => ({
        room_number: r.room_number,
        status: r.status,
        id: r.id,
        type: r.room_type || r.type
      })));
      
      setRooms(roomsData);
      setStatusOverrides((prev) => {
        const next = { ...prev };
        for (const room of roomsData) {
          const id = Number(room.id ?? room.room_id);
          if (!Number.isInteger(id)) continue;
          const backendStatus = normalizeRoomStatus(room.status);
          if (next[id] && next[id] === backendStatus) {
            delete next[id];
          }
        }
        return next;
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setError('Failed to load rooms');
    } finally {
      setLoading(false);
    }
  };

  const fetchFloors = async () => {
    try {
      const response = await requestWithAuth((headers) => axios.get(
        `${process.env.REACT_APP_API_URL}/api/manager/floors`,
        { headers }
      ));
      setFloors(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching floors:', err);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const response = await requestWithAuth((headers) => axios.get(
        `${process.env.REACT_APP_API_URL}/api/manager/room-types`,
        { headers }
      ));
      setRoomTypes(response.data.data || response.data);
    } catch (err) {
      console.error('Error fetching room types:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await requestWithAuth((headers) => axios.get(
        `${process.env.REACT_APP_API_URL}/api/analytics/rooms/status-distribution`,
        { headers }
      ));
      const statsObj = {};
      const dataArray = response.data.data || response.data || [];
      dataArray.forEach(item => {
        statsObj[item.status] = parseInt(item.count);
      });
      setStats(statsObj);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const normalizeRoomStatus = (status) => {
    const value = String(status || '').toLowerCase().trim();
    // Preserve all distinct status types
    if (value === 'out-of-service') return 'out_of_service';
    if (['available', 'reserved', 'booked', 'occupied', 'cleaning', 'maintenance', 'out_of_service'].includes(value)) return value;
    return 'available';
  };

  const updateRoomStatus = async (roomId, newStatus) => {
    const normalizedRoomId = Number(roomId);
    try {
      const mappedStatus = normalizeRoomStatus(newStatus);
      const room = rooms.find(r => Number(r.id ?? r.room_id) === normalizedRoomId);

      if (!room) {
        throw new Error('Room not found in local state');
      }

      setStatusOverrides((prev) => ({
        ...prev,
        [normalizedRoomId]: mappedStatus
      }));

      setRooms((prev) => prev.map((r) => {
        const id = Number(r.id ?? r.room_id);
        if (id !== normalizedRoomId) return r;
        return { ...r, status: mappedStatus };
      }));

      await requestWithAuth((headers) => axios.put(
        `${process.env.REACT_APP_API_URL}/api/manager/rooms/${normalizedRoomId}/status`,
        { status: mappedStatus },
        { headers }
      ));
      await fetchRooms();
      await fetchStats();
    } catch (err) {
      console.error('Error updating room status:', err);
      setStatusOverrides((prev) => {
        const next = { ...prev };
        delete next[normalizedRoomId];
        return next;
      });
      fetchRooms();
      alert('Failed to update room status');
    }
  };

  const getStatusColor = (status) => {
    const normalizedStatus = normalizeRoomStatus(status);
    const colors = {
      available: '#10b981',      // Green - available for booking
      reserved: '#8b5cf6',       // Purple - reserved but not checked in
      booked: '#3b82f6',         // Blue - booked/confirmed
      occupied: '#ef4444',       // Red - guest currently checked in
      cleaning: '#06b6d4',       // Cyan - housekeeping in progress
      maintenance: '#f59e0b',    // Orange - under maintenance/repair
      out_of_service: '#6b7280'  // Gray - out of service
    };
    return colors[normalizedStatus] || '#6b7280';
  };

  const getStatusLabel = (status) => {
    const normalizedStatus = normalizeRoomStatus(status);
    const labels = {
      available: 'Available',
      reserved: 'Reserved',
      booked: 'Booked',
      occupied: 'Occupied',
      cleaning: 'Cleaning',
      maintenance: 'Maintenance',
      out_of_service: 'Out of Service'
    };
    return labels[normalizedStatus] || normalizedStatus;
  };

  const getRoomId = (room) => room.id ?? room.room_id;

  const getRoomFloorLabel = (room) => {
    if (room.floor_name) return String(room.floor_name);
    if (room.floor !== undefined && room.floor !== null) return `Floor ${room.floor}`;
    if (room.floor_id !== undefined && room.floor_id !== null) return `Floor ${room.floor_id}`;
    if (room.hotel_floor_id !== undefined && room.hotel_floor_id !== null) return `Floor ${room.hotel_floor_id}`;
    return 'N/A';
  };

  const getRoomKey = (room, index) => {
    const roomId = getRoomId(room);
    if (roomId !== undefined && roomId !== null) return `room-${roomId}`;
    return `room-${room.hotel_id ?? 'h'}-${room.room_number ?? 'n'}-${index}`;
  };

  const getEffectiveRoomStatus = (room) => {
    const roomId = Number(getRoomId(room));
    if (Number.isInteger(roomId) && statusOverrides[roomId]) {
      return statusOverrides[roomId];
    }
    if (room.current_booking) {
      return 'booked';
    }
    return normalizeRoomStatus(room.status);
  };

  const filteredRooms = rooms.filter(room => {
    const matchesSearch = room.room_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         room.room_type?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || getEffectiveRoomStatus(room) === filterStatus;
    const matchesFloor = filterFloor === 'all' || getRoomFloorLabel(room) === filterFloor;
    return matchesSearch && matchesStatus && matchesFloor;
  });

  if (loading) {
    return (
      <div className="room-inventory-loading">
        <div className="loading-spinner"></div>
        <p>Loading room inventory...</p>
      </div>
    );
  }

  return (
    <div className="room-inventory-tab">
      {/* Header with Stats */}
      <div className="inventory-header">
        <h2>Room Inventory</h2>
        
        {/* Status Legend */}
        <div className="status-legend">
          <span className="legend-title">Status Legend:</span>
          <div className="legend-items">
            <div className="legend-item">
              <span className="legend-badge" style={{ backgroundColor: getStatusColor('available') }}></span>
              <span>Available</span>
            </div>
            <div className="legend-item">
              <span className="legend-badge" style={{ backgroundColor: getStatusColor('reserved') }}></span>
              <span>Reserved</span>
            </div>
            <div className="legend-item">
              <span className="legend-badge" style={{ backgroundColor: getStatusColor('booked') }}></span>
              <span>Booked</span>
            </div>
            <div className="legend-item">
              <span className="legend-badge" style={{ backgroundColor: getStatusColor('occupied') }}></span>
              <span>Occupied</span>
            </div>
            <div className="legend-item">
              <span className="legend-badge" style={{ backgroundColor: getStatusColor('cleaning') }}></span>
              <span>Cleaning</span>
            </div>
            <div className="legend-item">
              <span className="legend-badge" style={{ backgroundColor: getStatusColor('maintenance') }}></span>
              <span>Maintenance</span>
            </div>
            <div className="legend-item">
              <span className="legend-badge" style={{ backgroundColor: getStatusColor('out_of_service') }}></span>
              <span>Out of Service</span>
            </div>
          </div>
        </div>

        <div className="stats-cards">
          <div className="stat-card total">
            <span className="stat-value">{rooms.length}</span>
            <span className="stat-label">Total Rooms</span>
          </div>
          <div className="stat-card available">
            <span className="stat-value">{stats.available || 0}</span>
            <span className="stat-label">Available</span>
          </div>
          <div className="stat-card reserved">
            <span className="stat-value">{stats.reserved || 0}</span>
            <span className="stat-label">Reserved</span>
          </div>
          <div className="stat-card booked">
            <span className="stat-value">{stats.booked || 0}</span>
            <span className="stat-label">Booked</span>
          </div>
          <div className="stat-card occupied">
            <span className="stat-value">{stats.occupied || 0}</span>
            <span className="stat-label">Occupied</span>
          </div>
          <div className="stat-card cleaning">
            <span className="stat-value">{stats.cleaning || 0}</span>
            <span className="stat-label">Cleaning</span>
          </div>
          <div className="stat-card maintenance">
            <span className="stat-value">{stats.maintenance || 0}</span>
            <span className="stat-label">Maintenance</span>
          </div>
          <div className="stat-card out-of-service">
            <span className="stat-value">{stats.out_of_service || 0}</span>
            <span className="stat-label">Out of Service</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="inventory-toolbar">
        <div className="toolbar-left">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by room number or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="available">✓ Available</option>
            <option value="reserved">◆ Reserved</option>
            <option value="booked">● Booked</option>
            <option value="occupied">■ Occupied</option>
            <option value="cleaning">◐ Cleaning</option>
            <option value="maintenance">⚠ Maintenance</option>
            <option value="out_of_service">✕ Out of Service</option>
          </select>

          <select 
            value={filterFloor} 
            onChange={(e) => setFilterFloor(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Floors</option>
            {floors.map((floor, index) => {
              const floorLabel = floor.floor_name || `Floor ${floor.floor_id ?? floor.id ?? index + 1}`;
              const floorKey = floor.id ?? floor.floor_id ?? floorLabel;
              return (
              <option key={floorKey} value={floorLabel}>
                {floorLabel}
              </option>
              );
            })}
          </select>
        </div>

        <div className="toolbar-right">
          <button className="view-toggle" onClick={() => setViewMode('grid')} title="Grid View">
            <LayoutGrid size={18} className={viewMode === 'grid' ? 'active' : ''} />
          </button>
          <button className="view-toggle" onClick={() => setViewMode('list')} title="List View">
            <List size={18} className={viewMode === 'list' ? 'active' : ''} />
          </button>
          <button className="refresh-btn" onClick={fetchRooms}>
            <RefreshCw size={18} />
            Refresh
          </button>
          <button className="add-btn" onClick={() => setShowAddModal(true)}>
            <Plus size={18} />
            Add Room
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Room Display */}
      {viewMode === 'grid' ? (
        <div className="rooms-grid">
          {filteredRooms.map((room, index) => {
            const currentStatus = getEffectiveRoomStatus(room);
            return (
            <div 
              key={getRoomKey(room, index)} 
              className={`room-card room-status-${currentStatus}`}
              style={{ borderLeftColor: getStatusColor(currentStatus), borderLeftWidth: '4px' }}
            >
              <div className="room-card-header">
                <h3>{room.room_number}</h3>
                <span 
                  className="status-badge"
                  style={{ backgroundColor: getStatusColor(currentStatus) }}
                >
                  {getStatusLabel(currentStatus)}
                </span>
              </div>
              
              <div className="room-card-body">
                <div className="room-info">
                  <span className="label">Type:</span>
                  <span className="value">{room.room_type}</span>
                </div>
                <div className="room-info">
                  <span className="label">Floor:</span>
                  <span className="value">{getRoomFloorLabel(room)}</span>
                </div>
                <div className="room-info">
                  <span className="label">Capacity:</span>
                  <span className="value">{room.capacity} guests</span>
                </div>
                <div className="room-info">
                  <span className="label">Price:</span>
                  <span className="value">${room.price}/night</span>
                </div>
              </div>

              <div className="room-card-actions">
                <select 
                  value={currentStatus}
                  onChange={(e) => updateRoomStatus(getRoomId(room), e.target.value)}
                  className="status-select"
                >
                  <option value="available">Available</option>
                  <option value="reserved">Reserved</option>
                  <option value="booked">Booked</option>
                  <option value="occupied">Occupied</option>
                  <option value="cleaning">Cleaning</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="out_of_service">Out of Service</option>
                </select>
                <button 
                  className="edit-btn"
                  onClick={() => setSelectedRoom(room)}
                  title="Edit Room"
                >
                  <Edit2 size={16} />
                </button>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <div className="rooms-table-container">
          <table className="rooms-table">
            <thead>
              <tr>
                <th>Room Number</th>
                <th>Type</th>
                <th>Floor</th>
                <th>Capacity</th>
                <th>Price</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRooms.map((room, index) => (
                <tr key={getRoomKey(room, index)}>
                  <td><strong>{room.room_number}</strong></td>
                  <td>{room.room_type}</td>
                  <td>{getRoomFloorLabel(room)}</td>
                  <td>{room.capacity}</td>
                  <td>${room.price}</td>
                  <td>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(getEffectiveRoomStatus(room)) }}
                    >
                      {getStatusLabel(getEffectiveRoomStatus(room))}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button onClick={() => setSelectedRoom(room)} title="Edit">
                        <Edit2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {filteredRooms.length === 0 && !loading && (
        <div className="empty-state">
          <DoorOpen size={48} />
          <h3>No Rooms Found</h3>
          <p>Try adjusting your search or filters</p>
        </div>
      )}
    </div>
  );
};

export default RoomInventoryTab;
